import { CapacityTableColumns, InventoryItem } from '@/root/shared-types'
import { getInventoryValues, InventoryValues } from '@/root/shared-helpers/sail-select/getInventoryValues'
import { getResidentInventoryValues } from '@/root/shared-helpers/sail-select/getResidentInventoryValues'
import { isSailCancelled, isSailClosed } from '@/root/shared-helpers/sail-select/sailStatus/sailStatus'
import { isHeavyTrucksRestricted } from '@/root/shared-helpers/sail-select/showWarningsAndColumns'

export interface DepartureRowModel {
  /** Bicycle availability, or null when the bicycle column is hidden. */
  bicycleData: InventoryValues | null
  /** Car deck (CAR_DECK) availability, or null when the car column is hidden. */
  carData: InventoryValues | null
  /** Resident car reserve (RESIDENT inside CAR_DECK), or null when the car-resident column is hidden. */
  carResidentData: InventoryValues | null
  dangerousGoods: boolean
  isCancelled: boolean
  isClosed: boolean
  /** True when heavy trucks are restricted on this sail (restricted prices with a car deck present). */
  noHeavyTracks: boolean
  /** True when any shown category has explicit zero availability (amount === 0); an absent category (amount undefined) is a hidden cell, not sold out. */
  isSoldOut: boolean
  /** Passenger deck availability, or null when the passenger column is hidden. */
  passengerData: InventoryValues | null
  /** Resident reserve availability, or null when the resident column is hidden. */
  residentData: InventoryValues | null
}

// A category the sail doesn't offer (amount: undefined) is not applicable to this departure —
// treat it as a hidden cell (null), not a sold-out gauge. Only amount === 0 is truly sold out.
const nullIfAbsent = (data: InventoryValues): InventoryValues | null => (data.amount === undefined ? null : data)

const hasNoAvailability = (data: InventoryValues | null): boolean => data !== null && data.amount === 0

/**
 * Derives the presentational model for a single departure row from raw inventory.
 * Only the categories enabled in `showColumns` are resolved; the rest are null.
 */
export const getDepartureRowModel = (item: InventoryItem, showColumns: CapacityTableColumns): DepartureRowModel => {
  const isCancelled = isSailCancelled(item)
  const isClosed = isSailClosed(item)
  const dangerousGoods = Boolean(item.carDeck?.dangerousGoods || item.passengerDeck?.dangerousGoods)
  const noHeavyTracks = isHeavyTrucksRestricted(item.restrictedPrices, item.carDeck)

  const passengerData = showColumns.showPassenger
    ? nullIfAbsent(getInventoryValues({ deckAvailability: item.passengerDeck, inventoryClass: 'DECK' }))
    : null
  const residentData = showColumns.showResident ? nullIfAbsent(getResidentInventoryValues(item.passengerDeck)) : null
  const bicycleData = showColumns.showBicycle
    ? nullIfAbsent(getInventoryValues({ deckAvailability: item.carDeck, inventoryClass: 'BICYCLE' }))
    : null
  const carData = showColumns.showCar
    ? nullIfAbsent(getInventoryValues({ deckAvailability: item.carDeck, inventoryClass: 'CAR_DECK' }))
    : null
  const carResidentData = showColumns.showCarResident
    ? nullIfAbsent(getResidentInventoryValues(item.carDeck, 'CAR_DECK'))
    : null

  const isSoldOut =
    hasNoAvailability(passengerData) ||
    hasNoAvailability(residentData) ||
    hasNoAvailability(bicycleData) ||
    hasNoAvailability(carData) ||
    hasNoAvailability(carResidentData)

  return {
    isCancelled,
    isClosed,
    dangerousGoods,
    noHeavyTracks,
    passengerData,
    residentData,
    bicycleData,
    carData,
    carResidentData,
    isSoldOut,
  }
}
